import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import i18n from '../../i18n';

export const useCaptcha = () => {
  const [loadingCaptcha, setLoadingCaptcha] = useState(true);
  const [countdown, setCountdown] = useState(0);
  
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const imageUrlRef = useRef(null);
  const timerRef = useRef(null);
  const isFetchingRef = useRef(false); 

  const fetchAndAnimateCaptcha = useCallback(async (isAutoRefresh = false) => {
    if (isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    setLoadingCaptcha(true);

    if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
    if (timerRef.current) clearInterval(timerRef.current);

    try {
      const response = await axios.get(
        `/api/captcha/?_=${new Date().getTime()}`,
        { responseType: 'blob' }
      );

      const newImageUrl = URL.createObjectURL(response.data);
      imageUrlRef.current = newImageUrl;

      const img = new Image();

      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
          isFetchingRef.current = false;
          return;
        }

        const frameWidth = 150;
        const frameHeight = 60;
        const frameCount = 30;
        const frameDuration = 1000 / 60;

        let frameIndex = 0;
        let lastFrameTime = 0;

        canvas.width = frameWidth;
        canvas.height = frameHeight;
        const ctx = canvas.getContext('2d');

        const animate = (timestamp) => {
          if (!canvasRef.current) return;
          
          if (timestamp - lastFrameTime > frameDuration) {
            lastFrameTime = timestamp;
            ctx.clearRect(0, 0, frameWidth, frameHeight);
            ctx.drawImage(
              img,
              0,
              frameIndex * frameHeight,
              frameWidth,
              frameHeight,
              0,
              0,
              frameWidth,
              frameHeight
            );
            frameIndex = (frameIndex + 1) % frameCount;
          }
          animationFrameId.current = requestAnimationFrame(animate);
        };

        animationFrameId.current = requestAnimationFrame(animate);
        setLoadingCaptcha(false);
        isFetchingRef.current = false; 
        setCountdown(300); 
      };

      img.onerror = () => {
        toast.error(i18n.t('use_captcha.load_error', 'Не удалось загрузить изображение CAPTCHA.'));
        setLoadingCaptcha(false);
        isFetchingRef.current = false;
      };

      img.src = newImageUrl;
    } catch (error) {
      isFetchingRef.current = false;
      setLoadingCaptcha(false);
      
      if (isAutoRefresh) {
        console.warn('Не удалось автоматически обновить CAPTCHA.');
      } else if (error.response && error.response.status === 429) {
        toast.warn(i18n.t('use_captcha.too_many_requests', 'Слишком много запросов. Попробуйте через минуту.'));
      } else {
        toast.error(i18n.t('use_captcha.network_error', 'Сетевая ошибка при загрузке CAPTCHA.'));
      }
    }
  }, []);

  useEffect(() => {
    fetchAndAnimateCaptcha(false);

    return () => {
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
      if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchAndAnimateCaptcha]);

  useEffect(() => {
    if (countdown <= 0) return;

    timerRef.current = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [countdown]);

  useEffect(() => {
    if (countdown === 0 && !loadingCaptcha && !isFetchingRef.current) {
      toast.info(i18n.t('use_captcha.expired_refreshed', 'Код с картинки истек и был обновлен.'));
      fetchAndAnimateCaptcha(true);
    }
  }, [countdown, fetchAndAnimateCaptcha, loadingCaptcha]);

  return {
    loadingCaptcha,
    canvasRef,
    fetchCaptcha: fetchAndAnimateCaptcha,
    countdown,
  };
};

export default useCaptcha;